import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Phone, Mail, MapPin, Globe, Clock, Cog, Star } from "lucide-react";

/**
 * Read-only contact card — opens as a modal from a machine card's
 * "Contact tech" button or from anywhere we want tap-to-call / tap-to-email.
 * Fetches the full technician record (with linked_machines) if only an id is
 * given, otherwise renders directly from the provided record.
 */
const TechContactCard = ({ open, technicianId, technician, onClose }) => {
  const [tech, setTech] = useState(technician || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (technician && !technicianId) {
      setTech(technician);
      return;
    }
    if (!technicianId) return;
    setBusy(true);
    api
      .get(`/technicians/${technicianId}`)
      .then(({ data }) => setTech(data))
      .catch(() => setTech(null))
      .finally(() => setBusy(false));
  }, [open, technicianId, technician]);

  if (!open) return null;

  const t = tech;
  const linked = (t?.linked_machines || []).slice().sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="tech-contact-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card bg-white max-w-lg w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="tech-contact-card"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black flex-shrink-0">
          <div>
            <div className="label-eyebrow">Service technician</div>
            <h2 className="font-display text-2xl leading-tight mt-1">
              {t?.name || (busy ? "Loading…" : "Not found")}
            </h2>
            {t?.role && <div className="text-sm text-[#607087] mt-1">{t.role}</div>}
            {t?.supplier_name && (
              <div className="text-sm font-bold text-[#228EBF] mt-1">
                {t.supplier_name}
              </div>
            )}
          </div>
          <button
            data-testid="tech-contact-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {t && (
          <div className="p-5 flex flex-col gap-3 overflow-y-auto">
            {t.phone && (
              <a
                data-testid="tech-tap-call"
                href={`tel:${t.phone}`}
                className="nb-btn bg-[#EAF5FB] px-4 py-3 flex items-center gap-3 text-lg font-bold"
              >
                <Phone size={20} strokeWidth={2.5} className="text-[#228EBF]" />
                {t.phone}
                <span className="ml-auto text-xs font-normal text-[#607087]">
                  Tap to call
                </span>
              </a>
            )}
            {t.email && (
              <a
                data-testid="tech-tap-email"
                href={`mailto:${t.email}`}
                className="nb-btn bg-white px-4 py-3 flex items-center gap-3 font-bold"
              >
                <Mail size={18} strokeWidth={2.5} />
                {t.email}
                <span className="ml-auto text-xs font-normal text-[#607087]">
                  Tap to email
                </span>
              </a>
            )}
            {t.after_hours_phone && (
              <a
                data-testid="tech-tap-after-hours"
                href={`tel:${t.after_hours_phone}`}
                className="nb-btn bg-[#FFF7DB] px-4 py-3 flex items-center gap-3 font-bold"
              >
                <Clock size={18} strokeWidth={2.5} className="text-[#7A4A00]" />
                {t.after_hours_phone}
                <span className="ml-auto text-xs font-normal text-[#7A4A00]">
                  After-hours
                </span>
              </a>
            )}
            {t.address && (
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={16} strokeWidth={2.5} className="mt-1 flex-shrink-0" />
                <span>{t.address}</span>
              </div>
            )}
            {t.specialities?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {t.specialities.map((s) => (
                  <span
                    key={s}
                    className="border-2 border-black rounded-full px-2 py-0.5 bg-[#EAF5FB] text-xs font-bold"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
            {t.notes && (
              <div className="border-2 border-black rounded-lg p-3 bg-[#FFF9EC] text-sm whitespace-pre-wrap">
                {t.notes}
              </div>
            )}
            {linked.length > 0 && (
              <details
                className="border-t-2 border-black pt-3 group"
                open={linked.length <= 5}
                data-testid="tech-linked-machines-details"
              >
                <summary
                  className="label-eyebrow flex items-center gap-1.5 cursor-pointer select-none list-none"
                  data-testid="tech-linked-machines-toggle"
                >
                  <Cog size={12} strokeWidth={2.5} />
                  Services {linked.length} machine{linked.length === 1 ? "" : "s"}
                  <span className="ml-auto text-[10px] text-[#228EBF] font-bold group-open:hidden">
                    Tap to expand
                  </span>
                  <span className="ml-auto text-[10px] text-[#607087] font-bold hidden group-open:inline">
                    Tap to collapse
                  </span>
                </summary>
                <div
                  className="flex flex-col gap-1.5 mt-2 max-h-64 overflow-y-auto pr-1"
                  data-testid="tech-linked-machines"
                >
                  {linked.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`tech-linked-machine-${m.id}`}
                      className="border-2 border-black rounded-lg px-3 py-2 text-sm flex items-center gap-2"
                    >
                      {m.is_primary && (
                        <Star
                          size={12}
                          strokeWidth={2.5}
                          className="text-[#FFB300]"
                          fill="#FFB300"
                        />
                      )}
                      <span className="font-bold">{m.name}</span>
                      {m.category && (
                        <span className="text-[10px] text-[#607087]">
                          · {m.category}
                        </span>
                      )}
                      {m.last_service_at && (
                        <span className="ml-auto text-[10px] text-[#607087] font-mono-num">
                          Last: {new Date(m.last_service_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechContactCard;
